import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, CheckCircle, AlertCircle, Mail, Users, Shield, User } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useAcceptInvitation } from '@/hooks/useTeam'
import api from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

interface InvitationDetails {
  email: string
  role: 'admin' | 'member'
  tenant_name: string
  invited_by: string
  expires_in_days: number
  is_expired: boolean
  is_valid: boolean
  error?: string
}

export function AcceptInvitation() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()
  const { toast } = useToast()
  const acceptMutation = useAcceptInvitation()

  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Signup form state
  const [showSignup, setShowSignup] = useState(false)
  const [signupData, setSignupData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: ''
  })
  const [signupError, setSignupError] = useState('')

  // Fetch invitation details
  useEffect(() => {
    if (!token) return

    const fetchInvitationDetails = async () => {
      try {
        const response = await api.get(`/invitations/${token}/details/`)
        setInvitationDetails(response.data)
        setLoading(false)
      } catch (err: any) {
        setError(err.response?.data?.error || 'Invalid or expired invitation')
        setLoading(false)
      }
    }

    fetchInvitationDetails()
  }, [token])

  // Auto-accept if user is already logged in and invitation is valid
  useEffect(() => {
    if (!authLoading && user && invitationDetails && invitationDetails.is_valid && !acceptMutation.isSuccess) {
      handleAccept()
    }
  }, [authLoading, user, invitationDetails])

  const handleAccept = async () => {
    if (!token) return

    try {
      await acceptMutation.mutateAsync(token)
      toast({
        title: 'Invitation accepted',
        description: 'Welcome to the team!',
      })
      // Redirect to team dashboard after 2 seconds
      setTimeout(() => {
        navigate('/app/team')
      }, 2000)
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.error || 'Failed to accept invitation',
        variant: 'destructive',
      })
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setSignupError('')

    // Validation
    if (!signupData.first_name || !signupData.last_name || !signupData.password) {
      setSignupError('All fields are required')
      return
    }

    if (signupData.password !== signupData.confirm_password) {
      setSignupError('Passwords do not match')
      return
    }

    if (signupData.password.length < 8) {
      setSignupError('Password must be at least 8 characters')
      return
    }

    try {
      // Create account with invitation token
      const response = await api.post('/auth/signup-invitation/', {
        first_name: signupData.first_name,
        last_name: signupData.last_name,
        password: signupData.password,
        invitation_token: token
      })

      toast({
        title: 'Welcome!',
        description: response.data.message || 'Account created successfully',
      })

      // Redirect to tenant dashboard
      if (response.data.redirect_url) {
        setTimeout(() => {
          window.location.href = response.data.redirect_url
        }, 1000)
      }
    } catch (err: any) {
      setSignupError(err.response?.data?.error || 'Failed to create account')
    }
  }

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (error || (invitationDetails && !invitationDetails.is_valid)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>Invalid Invitation</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                {error || invitationDetails?.error || 'This invitation link is invalid or has expired.'}
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.location.href = '/'}
            >
              Go to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  // Success state (accepting...)
  if (acceptMutation.isPending || acceptMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              {acceptMutation.isSuccess ? (
                <>
                  <CheckCircle className="h-12 w-12 text-green-500" />
                  <div className="text-center">
                    <h3 className="text-lg font-semibold">Welcome to the team!</h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      Redirecting to dashboard...
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Accepting invitation...</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!invitationDetails) return null

  const getRoleIcon = (role: string) => {
    return role === 'admin' ? <Shield className="h-4 w-4" /> : <User className="h-4 w-4" />
  }

  const getRoleColor = (role: string) => {
    return role === 'admin' ? 'text-blue-600' : 'text-muted-foreground'
  }

  // Main invitation view
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <CardTitle>Team Invitation</CardTitle>
          </div>
          <CardDescription>
            You've been invited to join a team
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-muted/50">
              <Users className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">{invitationDetails.tenant_name}</div>
                <div className="text-sm text-muted-foreground">
                  Invited by {invitationDetails.invited_by}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <div className={getRoleColor(invitationDetails.role)}>
                {getRoleIcon(invitationDetails.role)}
              </div>
              <div className="flex-1">
                <div className="font-medium">
                  {invitationDetails.role === 'admin' ? 'Admin' : 'Member'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {invitationDetails.role === 'admin' 
                    ? 'Can invite members and manage team'
                    : 'Can access and use all features'}
                </div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground text-center">
              Invitation for: <span className="font-medium">{invitationDetails.email}</span>
            </div>

            {invitationDetails.expires_in_days > 0 && (
              <div className="text-xs text-center text-muted-foreground">
                Expires in {invitationDetails.expires_in_days} {invitationDetails.expires_in_days === 1 ? 'day' : 'days'}
              </div>
            )}
          </div>

          {/* Authentication section */}
          {!user ? (
            !showSignup ? (
              // Login or Signup prompt
              <div className="space-y-3">
                <Alert>
                  <AlertDescription>
                    To accept this invitation, you need to create an account or sign in.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Button
                    className="w-full"
                    onClick={() => setShowSignup(true)}
                  >
                    Create Account
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      // Save invitation token to session storage
                      sessionStorage.setItem('invitation_token', token || '')
                      window.location.href = '/login/'
                    }}
                  >
                    Already have an account? Sign In
                  </Button>
                </div>
              </div>
            ) : (
              // Signup form
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={signupData.first_name}
                    onChange={(e) => setSignupData({ ...signupData, first_name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={signupData.last_name}
                    onChange={(e) => setSignupData({ ...signupData, last_name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={signupData.confirm_password}
                    onChange={(e) => setSignupData({ ...signupData, confirm_password: e.target.value })}
                    required
                  />
                </div>

                {signupError && (
                  <Alert variant="destructive">
                    <AlertDescription>{signupError}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Button type="submit" className="w-full">
                    Create Account & Accept
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowSignup(false)}
                  >
                    Back
                  </Button>
                </div>
              </form>
            )
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
